import api from "@/libs/axios";
import { PaginatedList } from "../models/common/paginatedList";
import {
  ChantierListItem, ChantierDetail, ChantierStatut, ChantierPriorite,
  ChantierRecapBoard, ChantierBulletin,
  CategorieChantier, OrigineChantier, ProcessusChantier,
  CreateChantierModel, UpdateChantierModel,
  AddPlanActionModel, UpdatePlanActionModel,
  MesureEfficaciteWrite,
  NoteListItem, NoteDetail, CreateNoteModel, UpdateNoteModel,
} from "../models/chantiers/chantier";

const resource = "chantiers";

export interface ChantiersSearchParams {
  pageNumber?: number; pageSize?: number;
  categorieId?: string; origineId?: string; sousCategorie?: string;
  statut?: number; priorite?: number; visibilite?: number; search?: string;
  processusId?: string; projetId?: string;
  orderBy?: string; orderByDescending?: boolean;
}

export default class ChantiersResource {
  async search(params: ChantiersSearchParams): Promise<PaginatedList<ChantierListItem>> {
    const response = await api.get(`${resource}`, { params });
    return response.data;
  }
  // Historique des chantiers archivés (réservé aux admins chantier).
  async searchArchived(params: ChantiersSearchParams): Promise<PaginatedList<ChantierListItem>> {
    const response = await api.get(`${resource}/archived`, { params });
    return response.data;
  }
  async getById(id: string): Promise<ChantierDetail> {
    return (await api.get(`${resource}/${id}`)).data;
  }
  async downloadRecapPdf(id: string): Promise<Blob> {
    return (await api.get(`${resource}/${id}/recap-pdf`, { responseType: "blob" })).data;
  }
  async downloadAvancementsPdf(id: string, dateDebut: string, dateFin: string): Promise<Blob> {
    return (await api.get(`${resource}/${id}/recap-avancements-pdf`, {
      params: { dateDebut, dateFin },
      responseType: "blob",
    })).data;
  }
  async recapPublic(projetId?: string): Promise<ChantierRecapBoard> {
    return (await api.get(`${resource}/recap-public`, { params: projetId ? { projetId } : undefined })).data;
  }
  async bulletin(): Promise<ChantierBulletin | null> {
    return (await api.get(`${resource}/bulletin`)).data;
  }
  async regenerateBulletin(): Promise<ChantierBulletin> {
    return (await api.post(`${resource}/bulletin/generate`)).data;
  }
  async create(data: CreateChantierModel): Promise<string> {
    return (await api.post(`${resource}`, data)).data;
  }
  async update(id: string, data: UpdateChantierModel): Promise<string> {
    return (await api.put(`${resource}/${id}`, data)).data;
  }
  async delete(id: string): Promise<void> {
    await api.delete(`${resource}/${id}`);
  }
  // Archivage / désarchivage manuels (réservés aux admins chantier).
  async archive(id: string): Promise<void> {
    await api.put(`${resource}/${id}/archive`);
  }
  async unarchive(id: string): Promise<void> {
    await api.put(`${resource}/${id}/unarchive`);
  }
  async setParticipants(id: string, participantIds: string[]): Promise<string> {
    return (await api.put(`${resource}/${id}/participants`, { participantIds })).data;
  }
  // Mesure d'efficacité (1:0..1 sur le chantier)
  async setMesureEfficacite(id: string, data: MesureEfficaciteWrite): Promise<string> {
    return (await api.put(`${resource}/${id}/mesure-efficacite`, data)).data;
  }
  async deleteMesureEfficacite(id: string): Promise<void> {
    await api.delete(`${resource}/${id}/mesure-efficacite`);
  }
  // Notes libres du chantier (carte dédiée, édition en ligne)
  async updateNotes(id: string, notes: string): Promise<string> {
    return (await api.put(`${resource}/${id}/notes`, { notes })).data;
  }
  // Plans
  async addPlan(id: string, data: AddPlanActionModel): Promise<string> {
    return (await api.post(`${resource}/${id}/plans`, data)).data;
  }
  async updatePlan(id: string, planId: string, data: UpdatePlanActionModel): Promise<string> {
    return (await api.put(`${resource}/${id}/plans/${planId}`, data)).data;
  }
  async removePlan(id: string, planId: string): Promise<void> {
    await api.delete(`${resource}/${id}/plans/${planId}`);
  }
  // Documents (fichiers) d'un plan d'action
  async addPlanFichier(id: string, planId: string, file: File, libelle?: string): Promise<string> {
    const fd = new FormData();
    fd.append("Fichier", file);
    if (libelle) fd.append("Libelle", libelle);
    return (await api.post(`${resource}/${id}/plans/${planId}/fichiers`, fd)).data;
  }
  async transcribeAudio(blob: Blob, fileName: string): Promise<string> {
    const fd = new FormData();
    fd.append("Audio", blob, fileName);
    return (await api.post(`${resource}/transcription`, fd)).data.text;
  }
  async formatNote(content: string): Promise<string> {
    return (await api.post(`${resource}/format-note`, { content })).data.html;
  }
  async deletePlanFichier(id: string, planId: string, fichierId: string): Promise<void> {
    await api.delete(`${resource}/${id}/plans/${planId}/fichiers/${fichierId}`);
  }
  async downloadPlanFichier(id: string, planId: string, fichierId: string): Promise<string> {
    return (await api.get(`${resource}/${id}/plans/${planId}/fichiers/${fichierId}/download`)).data;
  }
  // Référentiels lecture
  async getStatuts(): Promise<ChantierStatut[]> {
    return (await api.get(`${resource}/statuts`)).data;
  }
  async getPriorites(): Promise<ChantierPriorite[]> {
    return (await api.get(`${resource}/priorites`)).data;
  }
  async getSousCategories(): Promise<string[]> {
    return (await api.get(`${resource}/sous-categories`)).data;
  }
  async listCategories(): Promise<PaginatedList<CategorieChantier>> {
    return (await api.get(`${resource}/categories`, { params: { pageSize: 200 } })).data;
  }
  async listOrigines(): Promise<PaginatedList<OrigineChantier>> {
    return (await api.get(`${resource}/origines`, { params: { pageSize: 200 } })).data;
  }
  // Référentiels admin (CRUD)
  async createCategorie(data: { libelle: string; couleur: string }): Promise<string> {
    return (await api.post(`${resource}/categories`, data)).data;
  }
  async updateCategorie(id: string, data: { libelle: string; couleur: string }): Promise<string> {
    return (await api.put(`${resource}/categories/${id}`, data)).data;
  }
  async deleteCategorie(id: string): Promise<void> { await api.delete(`${resource}/categories/${id}`); }
  async createOrigine(data: { libelle: string }): Promise<string> {
    return (await api.post(`${resource}/origines`, data)).data;
  }
  async updateOrigine(id: string, data: { libelle: string }): Promise<string> {
    return (await api.put(`${resource}/origines/${id}`, data)).data;
  }
  async deleteOrigine(id: string): Promise<void> { await api.delete(`${resource}/origines/${id}`); }
  async listProcessus(): Promise<PaginatedList<ProcessusChantier>> {
    return (await api.get(`${resource}/processus`, { params: { pageSize: 200 } })).data;
  }
  // Notes
  async listNotes(chantierId: string, planId: string, pageNumber = 1, pageSize = 50): Promise<PaginatedList<NoteListItem>> {
    return (await api.get(`${resource}/${chantierId}/plans/${planId}/notes`, { params: { pageNumber, pageSize } })).data;
  }
  async getNote(chantierId: string, planId: string, noteId: string): Promise<NoteDetail> {
    return (await api.get(`${resource}/${chantierId}/plans/${planId}/notes/${noteId}`)).data;
  }
  async createNote(chantierId: string, planId: string, data: CreateNoteModel): Promise<string> {
    return (await api.post(`${resource}/${chantierId}/plans/${planId}/notes`, data)).data;
  }
  async updateNote(chantierId: string, planId: string, noteId: string, data: UpdateNoteModel): Promise<string> {
    return (await api.put(`${resource}/${chantierId}/plans/${planId}/notes/${noteId}`, data)).data;
  }
  async deleteNote(chantierId: string, planId: string, noteId: string): Promise<void> {
    await api.delete(`${resource}/${chantierId}/plans/${planId}/notes/${noteId}`);
  }
}
